import { useEffect, useMemo, useState } from "react";
import { validateDefinition, type DefinitionProblem, type DefinitionValidation, type ProcessContentBody } from "../../../api/content/authoring/processAuthoringApi";
import type { ShapeViolations } from "../../../api/content/mergeBlockDecoders";
import { isAbortError } from "../../../api/http/httpClient";
import { toUiError, type UiError } from "../../../errors/uiError";
import { readableJsonText } from "../../../shared/formatting/readableJson";

/** How long a draft must stay unchanged before the server checks it (AU-3). */
export const CHECK_DELAY_MILLISECONDS = 400;
/** How much longer a draft without problems must stay unchanged before the review's shapes check it: they run the whole process graph. */
export const SHAPES_DELAY_MILLISECONDS = 1200;

/** What the review's shapes say about a draft without problems (reviews.md R-3). */
export type ShapesCheck =
  | { readonly status: "checking" }
  | { readonly status: "checked"; readonly shapes: ShapeViolations | null }
  | { readonly status: "failed"; readonly error: UiError };

/**
 * The server's check of the current draft: the same problems a commit would report, module B's
 * plans of a Workflow draft, the inputs it stores once one has a default and what fills each
 * step's inputs (`null` when nothing is known, WD-1+3 D-6, S4-G1, F102 CP-04), then the review's
 * shapes.
 */
export type DefinitionCheck =
  | { readonly status: "checking" }
  | ({ readonly status: "checked"; readonly shapes: ShapesCheck } & Omit<DefinitionValidation, "shapes">)
  | { readonly status: "failed"; readonly error: UiError };

/**
 * Check `content` with the server whenever it stays unchanged for a moment; an earlier
 * request is aborted when the draft changes again. A draft without problems that stays
 * unchanged a little longer is also checked with the review's shapes, so the editor says
 * what the merge would say (FR-MODEL-2). `content` is compared by its canonical JSON, so a
 * new object with the same definition starts no request.
 */
export function useDefinitionCheck(projectId: string, content: ProcessContentBody, itemId: string | null): DefinitionCheck {
  const key = useMemo(() => readableJsonText({ content, itemId }), [content, itemId]);
  const [result, setResult] = useState<{ readonly key: string; readonly check: DefinitionCheck } | null>(null);
  const [request, setRequest] = useState({ key, content, itemId });
  // A new definition replaces the pending request; render-time adjustment, not an effect.
  if (request.key !== key) {
    setRequest({ key, content, itemId });
  }

  useEffect(() => {
    const controller = new AbortController();
    const timers: number[] = [];
    const failed = (caught: unknown): UiError | null =>
      controller.signal.aborted || isAbortError(caught) ? null : toUiError(caught);
    timers.push(
      window.setTimeout(() => {
        validateDefinition(projectId, request.content, request.itemId, { signal: controller.signal }).then(
          ({ shapes: _none, ...answer }) => {
            if (answer.problems.length > 0) {
              setResult({ key: request.key, check: { status: "checked", ...answer, shapes: { status: "checked", shapes: null } } });
              return;
            }
            setResult({ key: request.key, check: { status: "checked", ...answer, shapes: { status: "checking" } } });
            timers.push(
              window.setTimeout(() => {
                validateDefinition(projectId, request.content, request.itemId, { checkShapes: true, signal: controller.signal }).then(
                  ({ shapes, ...checked }) => {
                    setResult({ key: request.key, check: { status: "checked", ...checked, shapes: { status: "checked", shapes } } });
                  },
                  (caught: unknown) => {
                    const error = failed(caught);
                    if (error !== null) {
                      setResult({ key: request.key, check: { status: "checked", ...answer, shapes: { status: "failed", error } } });
                    }
                  },
                );
              }, SHAPES_DELAY_MILLISECONDS),
            );
          },
          (caught: unknown) => {
            const error = failed(caught);
            if (error !== null) {
              setResult({ key: request.key, check: { status: "failed", error } });
            }
          },
        );
      }, CHECK_DELAY_MILLISECONDS),
    );
    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer);
      }
      controller.abort();
    };
  }, [projectId, request]);

  return result !== null && result.key === key ? result.check : { status: "checking" };
}

/** The tags of tagged unions in the server's paths: the item kind, then the step kind. */
const ITEM_KIND_TAG = /^(?:prompt|label_list|step|workflow|tool|skill|artifact)\./;
const STEP_KIND_TAG = /^(?:agent|human|tool)\./;

/**
 * A problem's path inside the definition the form edits. The server's paths start at the
 * item content and may name the union members they pass through, such as
 * `content.step.definition.agent.prompt`; both forms end as `prompt`.
 */
export function definitionPath(problem: DefinitionProblem): string {
  const inContent = problem.path.replace(/^content(?:\.|$)/, "").replace(ITEM_KIND_TAG, "");
  const inDefinition = inContent.replace(/^definition(?:\.|$)/, "");
  return inDefinition === inContent ? inDefinition : inDefinition.replace(STEP_KIND_TAG, "");
}

/** The problems that belong to one field of the form: those at `path` or inside it. */
export function problemsAt(problems: readonly DefinitionProblem[], path: string): readonly DefinitionProblem[] {
  return problems.filter((problem) => {
    const own = definitionPath(problem);
    return own === path || own.startsWith(`${path}.`);
  });
}
